import React, { useState } from 'react';
import { Alert, Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import e2iApi from '../../services/e2iApi';
import { ENQUIRY_TYPES } from '../../types/e2i';
import { formatDate, formatDateTime, partyLabel } from '../../utils/e2i';
import ActionDialog from './ActionDialog';
import ActivitiesEditor from './ActivitiesEditor';
import AttachmentsPanel from './AttachmentsPanel';
import EnquiryForm, { EnquiryFormState, enquiryProblems } from './EnquiryForm';
import InfoField from './InfoField';
import type { WorkspaceTabProps } from './workspace';

interface Props extends WorkspaceTabProps {
  form: EnquiryFormState;
  onFormChange: (next: EnquiryFormState) => void;
  dirty: boolean;
  busy: string | null;
  onSave: () => Promise<void>;
  onPost: () => Promise<void>;
}

/** The enquiry itself: its fields (editable while a draft), activities, files, posting and cancelling. */
const EnquiryTab: React.FC<Props> = ({ ws, config, companyId, onWorkspace, reload, notify, form, onFormChange, dirty, busy, onSave, onPost }) => {
  const [cancelOpen, setCancelOpen] = useState(false);
  const { enquiry: e, permissions: perms } = ws;
  const editable = e.status === 'Draft' && perms.canEdit;
  const postProblems = enquiryProblems(form, true);
  const typeLabel = ENQUIRY_TYPES.find((t) => t.value === e.enquiryType)?.label || e.enquiryType;
  const canFiles = e.status !== 'Cancelled' && (perms.canEdit || perms.canEstimate || perms.canQuote || perms.canCreateJob || perms.canCreateCir);

  const cancel = async ({ comment }: { comment: string }) => {
    const next = await e2iApi.cancelEnquiry(e.enquiryId, comment);
    onWorkspace(next);
    setCancelOpen(false);
    notify(`${next.enquiry.enquiryNo || 'The enquiry'} was cancelled`, 'success');
  };

  return (
    <Stack spacing={2}>
      {e.status === 'Cancelled' ? (
        <Alert severity="warning">Cancelled{e.cancelReason ? `: ${e.cancelReason}` : ''}</Alert>
      ) : null}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>Enquiry details</Typography>
        {editable ? (
          <>
            <EnquiryForm idPrefix="e2i-enq" companyId={companyId} config={config} value={form} onChange={onFormChange} />
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              {dirty ? <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>Unsaved changes</Typography> : null}
              {perms.canPost && postProblems.length ? (
                <Typography variant="caption" color="text.secondary" sx={{ mr: dirty ? 0 : 'auto' }}>To post: {postProblems.join('; ').toLowerCase()}.</Typography>
              ) : null}
              <Button variant="outlined" onClick={() => { void onSave(); }} disabled={!!busy || !dirty}>{busy === 'save' ? 'Saving…' : 'Save draft'}</Button>
              {perms.canPost ? (
                <Button onClick={() => { void onPost(); }} disabled={!!busy || postProblems.length > 0}>{busy === 'post' ? 'Posting…' : 'Post enquiry'}</Button>
              ) : null}
            </Stack>
          </>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InfoField label="Customer" value={partyLabel(e.customer)} hint={e.customer?.federalTaxId ? `VAT ${e.customer.federalTaxId}` : config.vatRegistered ? 'No VAT number in SAP' : undefined} />
            </Grid>
            <Grid item xs={12} md={6}><InfoField label="Project (main job)" value={partyLabel(e.project)} /></Grid>
            <Grid item xs={12} sm={6} md={4}><InfoField label="Enquiry type" value={typeLabel} /></Grid>
            <Grid item xs={6} md={4}><InfoField label="Enquiry date" value={formatDate(e.enquiryDate)} /></Grid>
            <Grid item xs={6} md={4}><InfoField label="Quotation due by" value={formatDate(e.quoteRequiredDate)} /></Grid>
            <Grid item xs={12}><InfoField label="Subject" value={e.subject} /></Grid>
            <Grid item xs={12} sm={4}><InfoField label="Contact person" value={e.contactPerson} /></Grid>
            <Grid item xs={12} sm={4}><InfoField label="Phone" value={e.phone} /></Grid>
            <Grid item xs={12} sm={4}><InfoField label="E-mail" value={e.email} /></Grid>
            <Grid item xs={12}><InfoField label="Remarks" value={e.remarks} multiline /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>Activities</Typography>
              <ActivitiesEditor
                idPrefix="e2i-enq-ro"
                rows={(e.activities || []).map((a) => ({ activity: a.activity, description: a.description || '' }))}
                onChange={() => undefined}
                disabled
              />
            </Grid>
          </Grid>
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2, mb: 0 }}>
          {e.createdByName ? `Created by ${e.createdByName} ` : 'Created '}{formatDateTime(e.createdAt)}
          {e.postedAt ? ` · posted ${formatDateTime(e.postedAt)}` : ''}
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <AttachmentsPanel
          id="e2i-enquiry-files"
          entity="enquiry"
          entityId={e.enquiryId}
          attachments={ws.attachments || []}
          canUpload={canFiles}
          canDelete={canFiles}
          onChanged={reload}
          title="Enquiry files"
          emptyText="No files yet (the client's request, drawings, photos)."
        />
      </Paper>

      {perms.canCancel && e.status !== 'Cancelled' ? (
        <Box>
          <Button variant="outlined" color="error" onClick={() => setCancelOpen(true)} disabled={!!busy}>Cancel enquiry</Button>
        </Box>
      ) : null}

      <ActionDialog
        open={cancelOpen}
        id="e2i-cancel-enquiry"
        title={`Cancel ${e.enquiryNo || 'the enquiry'}`}
        help="The enquiry gets register status 16 (Cancelled) and can no longer be quoted or invoiced."
        confirmLabel="Cancel enquiry"
        danger
        commentLabel="Reason"
        commentRequired
        onClose={() => setCancelOpen(false)}
        onConfirm={cancel}
      />
    </Stack>
  );
};

export default EnquiryTab;
